import Link from "@/components/feature/LocaleLink";
import PageLayout from "@/components/feature/PageLayout";
import { useLang } from "@/lib/useLang";
import { SERVICES, SITE_PHOTOS } from "@/data/services";
import imgHistory from "@/assets/images/service-history.jpg";
import imgRack from "@/assets/images/server-rack.jpg";
import KoreaMap from "@/components/feature/KoreaMap";
import ProcessStory from "@/components/feature/ProcessStory";

const SectionTitle = ({ children }: { children: string }) => (
  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">{children}</h2>
);

export default function ServicesPage() {
  const lang = useLang();
  const s = SERVICES[lang];

  return (
    <PageLayout eyebrow={s.eyebrow} title={s.title} desc={s.desc} seoTitle={s.seoTitle} seoDescription={s.seoDescription}>
      <div className="space-y-20">
        {/* 핵심 서비스 3블록 */}
        {s.blocks.map((b, bi) => (
          <section key={b.title} id={`block-${bi + 1}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4" data-reveal>
              <div className="text-accent-600 text-xs tracking-[0.3em] font-semibold mb-2">0{bi + 1}</div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">{b.title}</h2>
              <p className="text-foreground-600 leading-relaxed">{b.desc}</p>
              {bi === 1 && (
                <div className="rounded-lg overflow-hidden mt-6">
                  <img src={imgRack} alt={lang === "ko" ? "선박 서버 · 네트워크 장비 랙" : "Vessel server and network rack"} loading="lazy" decoding="async" className="w-full h-48 object-cover" />
                </div>
              )}
            </div>
            <div className={`lg:col-span-8 grid grid-cols-1 gap-4 ${b.items.length === 4 ? "md:grid-cols-2" : "md:grid-cols-3"}`} data-reveal data-reveal-stagger>
              {b.items.map((it) => (
                <div key={it.title} className="glow-card glow-card-light p-5 rounded-lg bg-background-100 border border-background-200">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2">{it.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{it.desc}</p>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* 운영 범위 */}
        <section id="scope" data-reveal>
          <SectionTitle>{s.scopeTitle}</SectionTitle>
          <p className="text-foreground-700 leading-relaxed mb-6 max-w-3xl">{s.scopeDesc}</p>
          <div className="overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-background-200">
                {s.scope.map((row) => (
                  <tr key={row.area} className="bg-background-50">
                    <th className="w-44 md:w-56 px-4 py-3 text-left font-semibold text-foreground-950 bg-background-100 align-top whitespace-nowrap">{row.area}</th>
                    <td className="px-4 py-3 text-foreground-700 leading-relaxed">{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 운영 프로세스 */}
        <section id="process">
          <div data-reveal>
            <SectionTitle>{s.processTitle}</SectionTitle>
          </div>
          {/* 스크롤 스토리텔링: 왼쪽 단계 목록 고정, 오른쪽 카드 스크롤에 따라 현재 단계 강조 */}
          <ProcessStory steps={s.process} />
        </section>

        {/* 파트너십 */}
        <section data-reveal>
          <SectionTitle>{s.partnershipTitle}</SectionTitle>
          <p className="text-foreground-700 leading-relaxed mb-6 max-w-3xl">{s.partnershipDesc}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-reveal data-reveal-stagger>
            {s.partnership.map((p) => (
              <div key={p.title} className="glow-card glow-card-light p-5 rounded-lg bg-background-100 border border-background-200">
                <h3 className="font-heading font-semibold text-foreground-950 mb-2">{p.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 이력 관리 */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-reveal>
          <div className="lg:col-span-6">
            <SectionTitle>{s.historyTitle}</SectionTitle>
            <p className="text-foreground-700 leading-relaxed mb-6">{s.historyDesc}</p>
            <ul className="space-y-4">
              {s.historyItems.map((h) => (
                <li key={h.title} className="flex gap-3">
                  <i className="ri-check-line text-accent-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-foreground-950">{h.title}</div>
                    <div className="text-sm text-foreground-600 leading-relaxed">{h.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 rounded-lg overflow-hidden border border-background-200">
            <img src={imgHistory} alt={lang === "ko" ? "선박 IT 서비스 이력 관리 화면" : "Vessel IT service history management"} loading="lazy" decoding="async" className="w-full object-cover" />
          </div>
        </section>

        {/* 서비스 지역 */}
        <section id="coverage" data-reveal>
          <SectionTitle>{s.areaTitle}</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* 인터랙티브 지도: 부산 본사에서 각 항만으로 이어지는 방선 지원망 */}
            <div className="glow-card lg:col-span-7 grid grid-cols-1 md:grid-cols-12 gap-6 p-6 md:p-8 rounded-xl bg-foreground-950 text-background-50 overflow-hidden">
              <div className="md:col-span-5 flex flex-col justify-center">
                <h3 className="font-heading text-xl md:text-2xl font-bold mb-3">{s.areaDomestic.title}</h3>
                <p className="text-sm text-foreground-300 leading-relaxed mb-5">{s.areaDomestic.desc}</p>
                <ul className="flex flex-wrap gap-2">
                  {s.areaDomestic.ports.split("·").map((p) => (
                    <li key={p} className="px-2.5 py-1 rounded-md bg-foreground-800 text-background-100 text-xs">{p.trim()}</li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-foreground-400 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[var(--brand-orange)]"></span>{lang === "ko" ? "본사" : "HQ"}</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-accent-300"></span>{lang === "ko" ? "방선 지원 항만" : "Supported ports"}</span>
                </p>
              </div>
              <div className="md:col-span-7 aspect-square md:aspect-auto md:min-h-[380px]">
                <KoreaMap
                  lang={lang}
                  hqLabel={lang === "ko" ? "본사 · 24시간 헬프데스크" : "Head office · 24h helpdesk"}
                  portLabel={lang === "ko" ? "정기 · 긴급 방선 지원" : "Scheduled & emergency visits"}
                />
              </div>
            </div>
            <div className="glow-card glow-card-light lg:col-span-5 p-6 rounded-lg bg-background-100 border border-background-200">
              <i className="ri-earth-line text-2xl text-primary-600"></i>
              <h3 className="font-heading text-xl font-bold text-foreground-950 mt-3 mb-2">{s.areaGlobal.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{s.areaGlobal.desc}</p>
            </div>
          </div>
        </section>

        {/* 현장 사진 */}
        <section id="gallery">
          <div data-reveal>
            <SectionTitle>{s.galleryTitle}</SectionTitle>
            <p className="text-foreground-700 leading-relaxed mb-6 max-w-3xl">{s.galleryDesc}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-reveal data-reveal-stagger>
            {SITE_PHOTOS.map((src, i) => (
              <figure key={src} className="rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img src={src} alt={s.galleryCaptions[i]} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
                <figcaption className="px-3 py-2 text-xs md:text-sm text-foreground-700">{s.galleryCaptions[i]}</figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {lang === "ko" ? "서비스 문의하기" : "Contact Us"}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
